/**
 * DTN Weather API Context Provider
 * Manages DTN API configuration and provides access to child components
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { DTNAuthConfig, DTNCatalog, DTNLayer } from './types';
import { useDTNToken, useDTNCatalog } from './useDTNApi';

interface DTNWeatherContextValue {
  config: DTNAuthConfig | null;
  token: string | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  catalog: DTNCatalog | undefined;
  availableLayers: DTNLayer[];
}

const DTNWeatherContext = createContext<DTNWeatherContextValue | undefined>(undefined);

export interface DTNWeatherProviderProps {
  children: ReactNode;
  config: DTNAuthConfig | null;
  locale?: string;
}

/**
 * Provider component for DTN Weather API
 * Handles authentication and provides context to child components
 */
export function DTNWeatherProvider({ 
  children, 
  config,
  locale = 'en-US'
}: DTNWeatherProviderProps) {
  const { 
    data: token, 
    isLoading: isTokenLoading, 
    error: tokenError 
  } = useDTNToken(config);

  const { 
    data: catalog, 
    isLoading: isCatalogLoading 
  } = useDTNCatalog(token, locale);

  const availableLayers = useMemo(() => {
    return catalog?.layers || [];
  }, [catalog]);

  const value = useMemo<DTNWeatherContextValue>(() => ({
    config,
    token,
    isAuthenticated: !!token,
    isLoading: isTokenLoading || isCatalogLoading,
    error: tokenError as Error | null,
    catalog,
    availableLayers,
  }), [config, token, isTokenLoading, isCatalogLoading, tokenError, catalog, availableLayers]);

  return (
    <DTNWeatherContext.Provider value={value}>
      {children}
    </DTNWeatherContext.Provider>
  );
}

/**
 * Hook to access DTN Weather context
 */
export function useDTNWeatherContext(): DTNWeatherContextValue {
  const context = useContext(DTNWeatherContext);
  if (!context) {
    throw new Error('useDTNWeatherContext must be used within a DTNWeatherProvider');
  }
  return context;
}

/**
 * Hook to check if DTN is configured and authenticated
 */
export function useIsDTNReady(): boolean {
  const context = useContext(DTNWeatherContext);
  return !!context?.isAuthenticated && !context?.isLoading;
}
